import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import { FaChevronDown, FaChevronUp, FaTimes, FaPlus, FaImage, FaList } from 'react-icons/fa';
import { FiMonitor, FiImage, FiMail, FiPhone, FiMapPin, FiHome, FiEdit2, FiTrash2 } from 'react-icons/fi';
import ReactQuill from 'react-quill-new';
import tempUploadService from '../../services/tempUploadService';
import 'react-quill-new/dist/quill.snow.css';
import FormSubmissionsModal from './FormSubmissionsModal';
import { useI18n } from '../../i18n';
import './CreateMinisiteModal.css';
import './AddPagesModal.css';

// Stable Quill config — must not be recreated per render or the editor remounts after each keystroke.
const QUILL_MODULES = {
    toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'clean'],
    ],
};

const QUILL_FORMATS = [
    'bold', 'italic', 'underline', 'strike',
    'list', 'link',
];

/* ─────────────────────────────────────────────────────────────
   RICH TEXT EDITOR  (About Us / What We Do / Documents)
   Uncontrolled Quill + ref: avoids cursor jump from controlled value resync.
───────────────────────────────────────────────────────────── */
const RichTextEditor = memo(({ page, onSaveContent, onLocalUpdate }) => {
    const { t } = useI18n();
    const contentRef = useRef(page.content || '');
    const [saving, setSaving] = useState(false);

    const handleChange = (html) => {
        contentRef.current = html;
    };

    const handleSave = async () => {
        if (!onSaveContent) return;
        const content = contentRef.current;
        setSaving(true);
        try {
            await onSaveContent(page.id, content);
            if (onLocalUpdate) onLocalUpdate(page.id, content);
        } finally { setSaving(false); }
    };

    return (
        <div className="ms-page-editor-wrapper">
            <div className="ms-page-editor-container">
                <ReactQuill
                    key={page.id}
                    theme="snow"
                    defaultValue={page.content || ''}
                    onChange={handleChange}
                    placeholder={t('pages.minisite.editor_placeholder')}
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                />
            </div>
            <button
                type="button"
                className="ms-page-editor-save-btn"
                onClick={handleSave}
                disabled={saving}
            >
                {saving ? t('common.status.saving') : t('common.actions.save')}
            </button>
        </div>
    );
});

/* ─────────────────────────────────────────────────────────────
   TEAM PAGE EDITOR
───────────────────────────────────────────────────────────── */
const emptyMember = () => ({ name: '', role: '', email: '', phone: '', image: null });

const TeamPageEditor = ({ page, onSaveTeam, onDeleteTeamMember }) => {
    const { t } = useI18n();
    const [selectedTeam, setSelectedTeam] = useState('Design Team');
    const [members, setMembers] = useState([emptyMember()]);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    // Initialize state if page content already has team and members
    useEffect(() => {
        if (page.content) {
            try {
                const data = JSON.parse(page.content);
                if (data.team) setSelectedTeam(data.team);
                if (data.members && data.members.length > 0) {
                    setMembers(data.members);
                }
            } catch (e) {
                // Not valid JSON or empty
            }
        }
    }, [page.content]);

    const updateMember = (index, field, value) => {
        setMembers(prev => prev.map((m, i) => i === index ? { ...m, [field]: value } : m));
        // Clear specific error field
        setErrors(prev => {
            const newErrs = { ...prev };
            delete newErrs[`members_array.${index}.${field}`];
            return newErrs;
        });
    };

    const handleImageChange = async (index, file) => {
        if (!file) return;

        // Instant local preview while upload inflight
        const localPreview = URL.createObjectURL(file);
        updateMember(index, 'image', localPreview);
        updateMember(index, 'isUploading', true);
        updateMember(index, 'image_temp_upload_id', null);

        try {
            const response = await tempUploadService.upload(file);
            const tempUpload = response.data;
            updateMember(index, 'image', tempUpload.file_url);
            updateMember(index, 'image_temp_upload_id', tempUpload.id);
            updateMember(index, 'isUploading', false);
        } catch (err) {
            updateMember(index, 'image', null);
            updateMember(index, 'isUploading', false);
            // Optionally set error
        }
    };

    const addMember = () => setMembers(prev => [...prev, emptyMember()]);

    const removeMember = async (index) => {
        const member = members[index];
        if (member.id && onDeleteTeamMember) {
            try {
                // If it fails, the catch block in MinisiteSection triggers the toast
                await onDeleteTeamMember(page.id, member.id);
            } catch (err) {
                return; // halt local removal if API failed
            }
        }
        setMembers(prev => prev.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        if (!onSaveTeam) return;
        setSaving(true);
        setErrors({});
        try {
            const payload = {
                team: selectedTeam,
                members: JSON.stringify(members),
            };
            await onSaveTeam(page.id, payload);
        } catch (err) {
            if (err?.status === 422 && err.errors) {
                setErrors(err.errors);
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="ms-team-editor">
            {/* Select Team */}
            <div className="ms-team-section">
                <label className="ms-team-label">{t('pages.minisite.select_team_label')}</label>
                <div className="ms-team-select-wrap">
                    <select
                        className={`ms-team-select${errors.team ? ' is-invalid' : ''}`}
                        value={selectedTeam}
                        onChange={e => setSelectedTeam(e.target.value)}
                    >
                        <option>Design Team</option>
                        <option>Engineering Team</option>
                        <option>Marketing Team</option>
                        <option>Sales Team</option>
                        <option>HR Team</option>
                    </select>
                    <FaChevronDown className="ms-team-select-icon" />
                </div>
                {errors.team && <div className="ms-cm-error">{errors.team[0]}</div>}
            </div>

            {/* Team Members */}
            <div className="ms-team-section">
                <label className="ms-team-label">{t('pages.minisite.add_member_details')}</label>

                {members.map((member, index) => (
                    <div key={index} className="ms-team-member-group">
                        {/* Row 1: Name, Role, Upload Image */}
                        <div className="ms-team-row">
                            <div className="ms-flex-1">
                                <input
                                    type="text"
                                    className={`ms-team-input${errors[`members_array.${index}.name`] ? ' is-invalid' : ''}`}
                                    placeholder="e.g. John Smith"
                                    value={member.name}
                                    onChange={e => updateMember(index, 'name', e.target.value)}
                                />
                                {errors[`members_array.${index}.name`] && <div className="ms-cm-error">{errors[`members_array.${index}.name`][0]}</div>}
                            </div>
                            <div className="ms-flex-1">
                                <input
                                    type="text"
                                    className={`ms-team-input${errors[`members_array.${index}.role`] ? ' is-invalid' : ''}`}
                                    placeholder="e.g UI/UX Designer"
                                    value={member.role}
                                    onChange={e => updateMember(index, 'role', e.target.value)}
                                />
                                {errors[`members_array.${index}.role`] && <div className="ms-cm-error">{errors[`members_array.${index}.role`][0]}</div>}
                            </div>
                            <div className="ms-flex-col">
                                <label className="ms-team-upload-btn">
                                    {member.isUploading ? (
                                        <div className="spinner-small" />
                                    ) : (
                                        <FaImage className="ms-team-upload-icon" />
                                    )}
                                    <span>{member.isUploading ? t('common.status.uploading') : (member.image ? t('pages.minisite.change_image') : t('pages.minisite.upload_image'))}</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        disabled={member.isUploading}
                                        onChange={e => handleImageChange(index, e.target.files[0])}
                                    />
                                </label>
                                {(errors[`members_array.${index}.image`] || errors[`members_array.${index}.image_temp_upload_id`]) && (
                                    <div className="ms-cm-error">
                                        {errors[`members_array.${index}.image`]?.[0] || errors[`members_array.${index}.image_temp_upload_id`]?.[0]}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Row 2: Email, Phone */}
                        <div className="ms-team-row ms-team-row-half">
                            <div className="ms-flex-1">
                                <input
                                    type="email"
                                    className={`ms-team-input${errors[`members_array.${index}.email`] ? ' is-invalid' : ''}`}
                                    placeholder="e.g. hr@company.com"
                                    value={member.email}
                                    onChange={e => updateMember(index, 'email', e.target.value)}
                                />
                                {errors[`members_array.${index}.email`] && <div className="ms-cm-error">{errors[`members_array.${index}.email`][0]}</div>}
                            </div>
                            <div className="ms-flex-1">
                                <input
                                    type="text"
                                    className={`ms-team-input${errors[`members_array.${index}.phone`] ? ' is-invalid' : ''}`}
                                    placeholder="e.g. +44 7123 456789"
                                    value={member.phone}
                                    onChange={e => updateMember(index, 'phone', e.target.value)}
                                />
                                {errors[`members_array.${index}.phone`] && <div className="ms-cm-error">{errors[`members_array.${index}.phone`][0]}</div>}
                            </div>
                        </div>

                        {/* Remove button — only visible when more than one member */}
                        {members.length > 1 && (
                            <div className="ms-team-member-remove-wrap">
                                <button
                                    type="button"
                                    className="ms-team-remove-btn"
                                    onClick={() => removeMember(index)}
                                    aria-label="Remove member"
                                >
                                    <FaTimes size={10} />
                                    {t('pages.minisite.remove')}
                                </button>
                            </div>
                        )}
                    </div>
                ))}

                <button type="button" className="ms-team-add-btn" onClick={addMember}>
                    <FaPlus size={11} />
                    {t('pages.minisite.add')}
                </button>
            </div>

            <button
                type="button"
                className="ms-page-editor-save-btn"
                onClick={handleSave}
                disabled={saving}
            >
                {saving ? t('common.status.saving') : t('common.actions.save')}
            </button>
        </div>
    );
};

/* ─────────────────────────────────────────────────────────────
   WHAT WE DO EDITOR
───────────────────────────────────────────────────────────── */
const emptyService = () => ({ title: '', description: '' });

const WhatWeDoEditor = ({ page, onSaveServices, onDeleteService }) => {
    const { t } = useI18n();
    const [services, setServices] = useState([emptyService()]);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (page.content) {
            try {
                const data = JSON.parse(page.content);
                if (data && data.length > 0) {
                    setServices(data);
                }
            } catch (e) {
                // Not valid JSON or empty
            }
        }
    }, [page.content]);

    const updateService = (index, field, value) => {
        setServices(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
        setErrors(prev => {
            const newErrs = { ...prev };
            delete newErrs[`services_array.${index}.${field}`];
            return newErrs;
        });
    };

    const addService = () => setServices(prev => [...prev, emptyService()]);

    const removeService = async (index) => {
        const service = services[index];
        if (service.id && onDeleteService) {
            try {
                await onDeleteService(page.id, service.id);
            } catch (err) {
                return;
            }
        }
        setServices(prev => prev.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        if (!onSaveServices) return;
        setSaving(true);
        setErrors({});
        try {
            const payload = {
                services: JSON.stringify(services),
            };
            await onSaveServices(page.id, payload);
        } catch (err) {
            if (err?.status === 422 && err.errors) {
                setErrors(err.errors);
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="ms-team-editor">
            <div className="ms-team-section">
                <label className="ms-team-label ms-wwd-label">{t('pages.minisite.services_label')}</label>

                {services.map((service, index) => (
                    <div key={index} className="ms-team-member-group">
                        <div className="ms-team-row ms-service-row">
                            <div className="ms-flex-1">
                                <input
                                    type="text"
                                    className={`ms-team-input${errors[`services_array.${index}.title`] ? ' is-invalid' : ''}`}
                                    placeholder="e.g. Recruitment"
                                    value={service.title}
                                    onChange={e => updateService(index, 'title', e.target.value)}
                                />
                                {errors[`services_array.${index}.title`] && <div className="ms-cm-error">{errors[`services_array.${index}.title`][0]}</div>}
                            </div>
                            <div className="ms-flex-2">
                                <input
                                    type="text"
                                    className={`ms-team-input${errors[`services_array.${index}.description`] ? ' is-invalid' : ''}`}
                                    placeholder="e.g. Attract, hire, and onboard top talent."
                                    value={service.description}
                                    onChange={e => updateService(index, 'description', e.target.value)}
                                />
                                {errors[`services_array.${index}.description`] && <div className="ms-cm-error">{errors[`services_array.${index}.description`][0]}</div>}
                            </div>
                        </div>

                        {services.length > 1 && (
                            <div className="ms-team-member-remove-wrap">
                                <button
                                    type="button"
                                    className="ms-team-remove-btn"
                                    onClick={() => removeService(index)}
                                    aria-label="Remove service"
                                >
                                    <FaTimes size={10} />
                                    {t('pages.minisite.remove')}
                                </button>
                            </div>
                        )}
                    </div>
                ))}

                <button type="button" className="ms-team-add-btn" onClick={addService}>
                    <FaPlus size={11} />
                    {t('pages.minisite.add')}
                </button>
            </div>

            <button
                type="button"
                className="ms-page-editor-save-btn"
                onClick={handleSave}
                disabled={saving}
            >
                {saving ? t('common.status.saving') : t('common.actions.save')}
            </button>
        </div>
    );
};

/* ─────────────────────────────────────────────────────────────
   DOCUMENT EDITOR
───────────────────────────────────────────────────────────── */
const emptyDocument = () => ({ file_url: null, description: '' });

const DocumentEditor = ({ page, onSaveDocuments, onDeleteDocument }) => {
    const { t } = useI18n();
    const [documents, setDocuments] = useState([emptyDocument()]);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (page.content) {
            try {
                const data = JSON.parse(page.content);
                if (data && data.length > 0) {
                    setDocuments(data);
                }
            } catch (e) {
                // Not valid JSON or empty
            }
        }
    }, [page.content]);

    const updateDocument = (index, field, value) => {
        setDocuments(prev => prev.map((d, i) => i === index ? { ...d, [field]: value } : d));
        setErrors(prev => {
            const newErrs = { ...prev };
            delete newErrs[`documents_array.${index}.${field}`];
            return newErrs;
        });
    };

    const handleFileChange = async (index, file) => {
        if (!file) return;

        updateDocument(index, 'isUploading', true);
        updateDocument(index, 'file_temp_upload_id', null);
        updateDocument(index, 'fileName', file.name);

        try {
            const response = await tempUploadService.upload(file);
            const tempUpload = response.data;
            updateDocument(index, 'file_url', tempUpload.file_url);
            updateDocument(index, 'file_temp_upload_id', tempUpload.id);
            updateDocument(index, 'isUploading', false);
        } catch (err) {
            updateDocument(index, 'file_url', null);
            updateDocument(index, 'isUploading', false);
            updateDocument(index, 'fileName', null);
        }
    };

    const addDocument = () => setDocuments(prev => [...prev, emptyDocument()]);

    const removeDocument = async (index) => {
        const document = documents[index];
        if (document.id && onDeleteDocument) {
            try {
                await onDeleteDocument(page.id, document.id);
            } catch (err) {
                return;
            }
        }
        setDocuments(prev => prev.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        if (!onSaveDocuments) return;
        setSaving(true);
        setErrors({});
        try {
            const payload = {
                documents: JSON.stringify(documents),
            };
            await onSaveDocuments(page.id, payload);
        } catch (err) {
            if (err?.status === 422 && err.errors) {
                setErrors(err.errors);
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="ms-team-editor">
            <div className="ms-team-section">
                {documents.map((document, index) => (
                    <div key={index} className="ms-document-group">
                        <label className="ms-doc-label">{t('pages.minisite.upload_document_label')}</label>
                        <div className="ms-team-row ms-team-row-full">
                            <div className="ms-flex-1">
                                <label className={`ms-team-upload-btn ms-team-input-full ${errors[`documents_array.${index}.file_url`] || errors[`documents_array.${index}.file_temp_upload_id`] ? 'is-invalid' : ''}`}>
                                    {document.isUploading ? (
                                        <div className="spinner-small" />
                                    ) : (
                                        <FaImage className="ms-team-upload-icon" />
                                    )}
                                    <span>{document.isUploading ? t('common.status.uploading') : (document.fileName ? document.fileName : (document.file_url ? t('pages.minisite.change_file') : t('pages.minisite.upload_image')))}</span>
                                    <input
                                        type="file"
                                        hidden
                                        disabled={document.isUploading}
                                        onChange={e => handleFileChange(index, e.target.files[0])}
                                    />
                                </label>
                                {(errors[`documents_array.${index}.file_url`] || errors[`documents_array.${index}.file_temp_upload_id`]) && (
                                    <div className="ms-cm-error">
                                        {errors[`documents_array.${index}.file_url`]?.[0] || errors[`documents_array.${index}.file_temp_upload_id`]?.[0]}
                                    </div>
                                )}
                            </div>
                        </div>

                        <label className="ms-doc-label ms-doc-label--mt">{t('pages.minisite.document_description')}</label>
                        <div className="ms-team-row ms-team-row-full">
                            <div className="ms-flex-1">
                                <textarea
                                    className={`ms-team-textarea ${errors[`documents_array.${index}.description`] ? 'is-invalid' : ''}`}
                                    placeholder="for e.g. Use this form to gather details from applicants interested in the Content Writer role."
                                    value={document.description || ''}
                                    onChange={e => updateDocument(index, 'description', e.target.value)}
                                    rows={4}
                                />
                                {errors[`documents_array.${index}.description`] && <div className="ms-cm-error">{errors[`documents_array.${index}.description`][0]}</div>}
                            </div>
                        </div>

                        {documents.length > 1 && (
                            <div className="ms-team-member-remove-wrap ms-doc-remove-wrap--mt">
                                <button
                                    type="button"
                                    className="ms-team-remove-btn"
                                    onClick={() => removeDocument(index)}
                                    aria-label="Remove document"
                                >
                                    <FaTimes size={10} />
                                    {t('pages.minisite.remove')}
                                </button>
                            </div>
                        )}
                    </div>
                ))}

                <button type="button" className="ms-team-add-btn ms-doc-add-btn" onClick={addDocument}>
                    <FaPlus size={11} />
                    {t('pages.minisite.add')}
                </button>
            </div>

            <button
                type="button"
                className="ms-page-editor-save-btn"
                onClick={handleSave}
                disabled={saving}
            >
                {saving ? t('common.status.saving') : t('common.actions.save')}
            </button>
        </div>
    );
};

/* ─────────────────────────────────────────────────────────────
   ADD SECTION EDITOR (FORM BUILDER)
───────────────────────────────────────────────────────────── */
const AddSectionEditor = ({ page, onSaveContent, onLocalUpdate }) => {
    const { t } = useI18n();
    const defaultForm = { title: '', fields: [{ id: Date.now().toString(), label: '', type: 'text', required: false, options: '' }] };

    // Convert old array fallback if existing data matches old schema
    const parseInitialData = () => {
        try {
            const parsed = JSON.parse(page.content);
            if (Array.isArray(parsed) && parsed.length > 0) {
                // Return a clean slate or migrate the first one if we wanted
                return defaultForm;
            }
            if (parsed && typeof parsed === 'object' && parsed.fields) {
                return parsed;
            }
            return defaultForm;
        } catch (e) {
            return defaultForm;
        }
    };

    const [formConfig, setFormConfig] = useState(parseInitialData);
    const [saving, setSaving] = useState(false);

    const updateTitle = (val) => setFormConfig(prev => ({ ...prev, title: val }));

    const updateField = (id, key, val) => {
        setFormConfig(prev => ({
            ...prev,
            fields: prev.fields.map(f => f.id === id ? { ...f, [key]: val } : f)
        }));
    };

    const addField = () => {
        setFormConfig(prev => ({
            ...prev,
            fields: [...prev.fields, { id: Date.now().toString() + Math.random(), label: '', type: 'text', required: false, options: '' }]
        }));
    };

    const removeField = (id) => {
        setFormConfig(prev => ({
            ...prev,
            fields: prev.fields.filter(f => f.id !== id)
        }));
    };

    const handleSave = async () => {
        if (!onSaveContent) return;
        setSaving(true);
        try {
            const jsonString = JSON.stringify(formConfig);
            await onSaveContent(page.id, jsonString);
            if (onLocalUpdate) onLocalUpdate(page.id, jsonString);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="ms-page-editor">
            <div className="ms-form-builder-container">
                <div className="ms-form-builder-title-group">
                    <label className="ms-team-label ms-form-builder-label">{t('pages.minisite.form_title_label')}</label>
                    <input
                        type="text"
                        className="ms-team-input"
                        placeholder="e.g., Contact Us, Join the Team"
                        value={formConfig.title}
                        onChange={(e) => updateTitle(e.target.value)}
                    />
                </div>

                <div className="ms-team-label ms-form-builder-section-label">{t('pages.minisite.form_fields_label')}</div>
                <div className="ms-form-builder-fields">
                    {formConfig.fields.map((field, idx) => (
                        <div key={field.id} className="ms-form-builder-field-row">
                            <div>
                                <input
                                    type="text"
                                    className="ms-team-input ms-form-builder-input"
                                    placeholder="Field Label (e.g. Full Name)"
                                    value={field.label}
                                    onChange={(e) => updateField(field.id, 'label', e.target.value)}
                                />
                            </div>
                            <div>
                                <select
                                    className="ms-team-select ms-form-builder-select"
                                    value={field.type}
                                    onChange={(e) => updateField(field.id, 'type', e.target.value)}
                                >
                                    <option value="text">Short Text</option>
                                    <option value="textarea">Paragraph</option>
                                    <option value="email">Email</option>
                                    <option value="date">Date</option>
                                    <option value="checkbox">Checkbox</option>
                                    <option value="radio">Radio Buttons</option>
                                    <option value="select">Dropdown</option>
                                </select>
                            </div>
                            <div className="ms-form-builder-required">
                                <input
                                    type="checkbox"
                                    id={`req-${field.id}`}
                                    checked={field.required}
                                    onChange={(e) => updateField(field.id, 'required', e.target.checked)}
                                />
                                <label htmlFor={`req-${field.id}`} className="ms-form-builder-req-label">{t('pages.minisite.field_required')}</label>
                            </div>
                            <div>
                                <button
                                    type="button"
                                    className="ms-team-remove-btn ms-form-builder-remove-btn"
                                    onClick={() => removeField(field.id)}
                                    title="Remove field"
                                    disabled={formConfig.fields.length === 1}
                                >
                                    <FaTimes size={14} />
                                </button>
                            </div>

                            {/* Options row for types that require choices */}
                            {['select', 'radio', 'checkbox'].includes(field.type) && (
                                <div className="ms-form-builder-options-row">
                                    <input
                                        type="text"
                                        className="ms-team-input ms-form-builder-options-input"
                                        placeholder="Comma-separated options (e.g. Option A, Option B)"
                                        value={field.options}
                                        onChange={(e) => updateField(field.id, 'options', e.target.value)}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                <button type="button" className="ms-team-add-btn ms-form-builder-add-btn" onClick={addField}>
                    <FaPlus size={11} />
                    {t('pages.minisite.add_field')}
                </button>
            </div>

            <div className="ms-form-builder-footer">
                <button
                    type="button"
                    className="ms-page-editor-save-btn ms-form-builder-save-btn"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? t('common.status.saving') : t('pages.minisite.save_form')}
                </button>
            </div>
        </div>
    );
};

/* ─────────────────────────────────────────────────────────────
   DISPATCHER — pick the right body editor by page slug
───────────────────────────────────────────────────────────── */
const PageBodyRenderer = ({ page, onSaveContent, onSaveTeam, onDeleteTeamMember, onSaveServices, onDeleteService, onSaveDocuments, onDeleteDocument, onLocalUpdate }) => {
    switch (page.slug) {
        case 'team':
            return <TeamPageEditor page={page} onSaveTeam={onSaveTeam} onDeleteTeamMember={onDeleteTeamMember} />;
        case 'what-we-do':
            return <WhatWeDoEditor page={page} onSaveServices={onSaveServices} onDeleteService={onDeleteService} />;
        case 'documents':
            return <DocumentEditor page={page} onSaveDocuments={onSaveDocuments} onDeleteDocument={onDeleteDocument} />;
        case 'add-section':
            return <AddSectionEditor page={page} onSaveContent={onSaveContent} onLocalUpdate={onLocalUpdate} />;
        default:
            return <RichTextEditor page={page} onSaveContent={onSaveContent} onLocalUpdate={onLocalUpdate} />;
    }
};

/* ─────────────────────────────────────────────────────────────
   MAIN MODAL
───────────────────────────────────────────────────────────── */
const AddPagesModal = ({ isOpen, onClose, onTogglePublish, onStatusChange, onSetHome, onSaveContent, onSaveTeam, onDeleteTeamMember, onSaveServices, onDeleteService, onSaveDocuments, onDeleteDocument, loading, minisite }) => {
    const { t } = useI18n();
    const [pages, setPages] = useState([]);
    const [expandedPageId, setExpandedPageId] = useState(null);
    const [showSubmissionsModal, setShowSubmissionsModal] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setExpandedPageId(null);
            return;
        }
        if (minisite?.pages) {
            setPages(minisite.pages.map(p => ({
                id: p.id,
                title: p.title,
                slug: p.slug,
                status: Boolean(p.status),
                isHome: Boolean(p.is_home),
                content: p.content || '',
            })));
        }
    }, [isOpen, minisite?.id]);

    // After a successful save, keep local state in sync so collapsed/re-expanded
    // accordion still shows the saved content without a full reload.
    const handleLocalUpdate = useCallback((pageId, newContent) => {
        setPages(prev => prev.map(p =>
            p.id === pageId ? { ...p, content: newContent } : p
        ));
    }, []);

    if (!isOpen) return null;

    const toggleAccordion = (id, e) => {
        if (e.target.closest('.ms-page-checkbox-wrap') || e.target.closest('.ms-page-home-btn')) return;
        setExpandedPageId(prev => (prev === id ? null : id));
    };

    const toggleCheckbox = (id) => {
        const page = pages.find(p => p.id === id);
        if (!page) return;
        const newStatus = !page.status;
        setPages(prev => prev.map(p => p.id === id ? { ...p, status: newStatus } : p));
        if (onStatusChange) onStatusChange([{ id, status: newStatus }]);
    };

    const setAsHome = (id) => {
        if (onSetHome) onSetHome(id);
    };

    return (
        <div className="ms-cm-overlay ms-page-details-overlay" onClick={onClose}>
            <div className="ms-cm-container ms-add-pages-container ms-page-details-dialog" onClick={e => e.stopPropagation()}>

                {/* ── Header ── */}
                <div className="ms-cm-header ms-cm-header--blue">
                    <h2 className="ms-cm-title ms-cm-title--flex">
                        {t('pages.minisite.page_details_title')}
                        {minisite?.id && (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); if (onTogglePublish) onTogglePublish(); }}
                                disabled={loading}
                                title={minisite.status === 'published' ? t('pages.minisite.tooltip_set_draft') : t('pages.minisite.tooltip_publish')}
                                className={`ms-apm-publish-badge ${minisite.status === 'published' ? 'ms-apm-publish-badge--published' : 'ms-apm-publish-badge--draft'}`}
                            >
                                {minisite.status === 'published' ? t('pages.minisite.status_published') : t('pages.minisite.status_draft')}
                            </button>
                        )}
                    </h2>
                    <button type="button" className="ms-cm-close ms-cm-close--white" onClick={onClose} aria-label="Close">
                        <FaTimes size={14} />
                    </button>
                </div>

                {/* ── Body (scrollable) ── */}
                <div className="ms-cm-body ms-add-pages-body">
                    {/* Site Details */}
                    <div className="ms-details-meta">
                        <div className="ms-details-meta-row">
                            <div className="ms-details-meta-key">
                                <FiMonitor size={16} color="#4b5563" /> {t('pages.minisite.meta_site_name')}
                            </div>
                            <div className="ms-details-meta-val">{minisite?.title || ''}</div>
                        </div>
                        <div className="ms-details-meta-row">
                            <div className="ms-details-meta-key">
                                <FiImage size={16} color="#4b5563" /> {t('pages.minisite.meta_site_logo')}
                            </div>
                            <div>
                                {minisite?.logo_url ? (
                                    <a href={minisite.logo_url} target="_blank" rel="noreferrer" className="ms-details-logo-link">{t('pages.minisite.view_logo')}</a>
                                ) : (
                                    <span className="ms-details-no-logo">{t('pages.minisite.no_logo')}</span>
                                )}
                            </div>
                        </div>
                        <div className="ms-details-meta-row">
                            <div className="ms-details-meta-key">
                                <FiMail size={16} color="#4b5563" /> {t('pages.minisite.meta_email')}
                            </div>
                            <div className="ms-details-meta-val">{minisite?.contact_email || ''}</div>
                        </div>
                        <div className="ms-details-meta-row">
                            <div className="ms-details-meta-key">
                                <FiPhone size={16} color="#4b5563" /> {t('pages.minisite.meta_phone')}
                            </div>
                            <div className="ms-details-meta-val">{minisite?.contact_phone || ''}</div>
                        </div>
                        <div className="ms-details-meta-row">
                            <div className="ms-details-meta-key">
                                <FiMapPin size={16} color="#4b5563" /> {t('pages.minisite.meta_location')}
                            </div>
                            <div className="ms-details-meta-val">{minisite?.contact_address || ''}</div>
                        </div>
                    </div>

                    {pages.map((page) => {
                        const isExpanded = expandedPageId === page.id;
                        return (
                            <div key={page.id} className={`ms-page-accordion ${isExpanded ? 'expanded' : ''}`}>
                                {/* Accordion Header */}
                                <div
                                    className="ms-page-accordion-header"
                                    onClick={(e) => toggleAccordion(page.id, e)}
                                >
                                    <div className="ms-page-checkbox-wrap" onClick={(e) => { e.stopPropagation(); toggleCheckbox(page.id); }}>
                                        <div className={`ms-page-toggle-switch ${page.status ? 'active' : ''}`}>
                                            <div className="ms-page-toggle-dot"></div>
                                        </div>
                                    </div>

                                    <div className="ms-page-title">{page.slug === 'add-section' ? t('pages.minisite.add_form_label') : page.title}</div>

                                    {page.slug !== 'add-section' ? (
                                        <button
                                            className={`ms-page-home-btn ${page.isHome ? 'is-home' : 'not-home'}`}
                                            aria-label={page.isHome ? t('pages.minisite.your_home_page') : t('pages.minisite.set_as_home')}
                                            title={page.isHome ? t('pages.minisite.your_home_page') : t('pages.minisite.set_as_home')}
                                            onClick={(e) => { e.stopPropagation(); setAsHome(page.id); }}
                                        >
                                            <div className="ms-page-home-icon" aria-hidden="true" />
                                            <span className="ms-page-home-label-full" aria-hidden="true">{page.isHome ? t('pages.minisite.your_home_page') : t('pages.minisite.set_as_home')}</span>
                                            <span className="ms-page-home-label-compact" aria-hidden="true">{page.isHome ? t('pages.minisite.home_short') : t('pages.minisite.set_home_short')}</span>
                                        </button>
                                    ) : (
                                        <button
                                            className="ms-page-home-btn not-home ms-apm-submissions-btn"
                                            aria-label={t('pages.minisite.view_submissions')}
                                            title={t('pages.minisite.view_submissions')}
                                            onClick={(e) => { e.stopPropagation(); setShowSubmissionsModal(true); }}
                                        >
                                            <FaList aria-hidden="true" />
                                            <span className="ms-page-home-label-full" aria-hidden="true">{t('pages.minisite.view_submissions')}</span>
                                            <span className="ms-page-home-label-compact" aria-hidden="true">{t('pages.minisite.submissions_short')}</span>
                                        </button>
                                    )}

                                    <div className="ms-page-accordion-toggle">
                                        {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                    </div>
                                </div>

                                {/* Accordion Body */}
                                {isExpanded && (
                                    <div className="ms-page-accordion-body">
                                        <PageBodyRenderer
                                            page={page}
                                            onSaveContent={onSaveContent}
                                            onSaveTeam={onSaveTeam}
                                            onDeleteTeamMember={onDeleteTeamMember}
                                            onSaveServices={onSaveServices}
                                            onDeleteService={onDeleteService}
                                            onSaveDocuments={onSaveDocuments}
                                            onDeleteDocument={onDeleteDocument}
                                            onLocalUpdate={handleLocalUpdate}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* ── Footer ── */}
                <div className="ms-cm-footer">
                    <button type="button" className="ms-cm-btn ms-cm-btn-cancel" onClick={onClose} disabled={loading}>
                        {t('common.actions.close')}
                    </button>
                </div>
            </div>

            {showSubmissionsModal && minisite?.id && (
                <FormSubmissionsModal
                    minisiteId={minisite.id}
                    onClose={() => setShowSubmissionsModal(false)}
                />
            )}
        </div>
    );
};

export default AddPagesModal;
